import { useMemo } from 'react'
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from 'recharts'
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/components/ui/chart'
import type { TimeSeries } from '@/services/stats/statsQueryResult'

/**
 * Series colours in fixed order, so the busiest object always wears the first
 * one. "Other" is the sum of whatever did not fit and stays neutral, so it
 * never reads as one more object.
 */
const SERIES_COLORS = [
  'var(--ad-color-1)',
  'var(--ad-color-2)',
  'var(--ad-color-3)',
  'var(--ad-color-4)',
  'var(--ad-color-5)',
]
const OTHER_COLOR = 'var(--ad-text-faint)'

export interface TimeSeriesChartProps {
  data: TimeSeries
  /** What a series with no name of its own (the lone total line) is called. */
  metricLabel: string
  otherLabel: string
  /** Value as shown on the axis and in the tooltip. */
  formatValue: (value: number) => string
  /** Compact value for the axis. Defaults to `formatValue`. */
  formatAxisValue?: (value: number) => string
  /** Bucket start as a short axis label. */
  formatTick: (iso: string) => string
  /** Bucket start as the tooltip heading. */
  formatTime: (iso: string) => string
  className?: string
}

/**
 * One metric over time, as a line per series. One axis only: a second metric
 * belongs in its own chart, not on a second scale beside this one.
 */
export function TimeSeriesChart({
  data,
  metricLabel,
  otherLabel,
  formatValue,
  formatAxisValue = formatValue,
  formatTick,
  formatTime,
  className,
}: TimeSeriesChartProps) {
  const config = useMemo<ChartConfig>(() => {
    let colorIndex = 0
    return Object.fromEntries(
      data.series.map((s) => [
        s.key,
        {
          label: s.isOthers ? otherLabel : s.name || metricLabel,
          color: s.isOthers ? OTHER_COLOR : SERIES_COLORS[colorIndex++ % SERIES_COLORS.length],
        },
      ])
    )
  }, [data.series, metricLabel, otherLabel])

  const multiple = data.series.length > 1
  // A single bucket has nothing to join, so it gets a visible point.
  const lonePoint = data.points.length === 1

  return (
    <ChartContainer config={config} className={className ?? 'aspect-auto h-72 w-full'}>
      <LineChart data={data.points} margin={{ top: 8, right: 16, left: 8, bottom: 0 }}>
        <CartesianGrid vertical={false} />
        <XAxis
          dataKey="time"
          tickLine={false}
          axisLine={false}
          tickMargin={8}
          minTickGap={24}
          tickFormatter={formatTick}
        />
        <YAxis
          tickLine={false}
          axisLine={false}
          tickMargin={4}
          width={64}
          tickFormatter={(v: number) => formatAxisValue(v)}
        />
        <ChartTooltip
          cursor
          content={
            <ChartTooltipContent
              labelFormatter={(_, payload) => {
                const time = payload?.[0]?.payload?.time
                return typeof time === 'string' ? formatTime(time) : ''
              }}
              formatter={(value, name, item) => (
                <div className="flex w-full items-center gap-2">
                  <span
                    className="size-2.5 shrink-0 rounded-xs"
                    style={{ backgroundColor: item.color }}
                  />
                  <span className="text-muted-foreground">
                    {config[String(name)]?.label ?? name}
                  </span>
                  <span className="ml-auto font-mono font-medium tabular-nums text-foreground">
                    {formatValue(Number(value))}
                  </span>
                </div>
              )}
            />
          }
        />
        {multiple && <ChartLegend content={<ChartLegendContent />} />}
        {data.series.map((s) => (
          <Line
            key={s.key}
            dataKey={s.key}
            type="monotone"
            stroke={`var(--color-${s.key})`}
            strokeWidth={2}
            strokeDasharray={s.isOthers ? '4 3' : undefined}
            dot={lonePoint ? { r: 4 } : false}
            activeDot={{ r: 4, strokeWidth: 2, stroke: 'var(--ad-surface)' }}
            isAnimationActive={false}
          />
        ))}
      </LineChart>
    </ChartContainer>
  )
}
